import React from 'react';
import { ArrowUpRight, Check } from 'lucide-react';
import styles from './Home.module.css';
import Button from '../../components/Button/Button';
import Hero from '../../components/Hero/Hero';
import Marquee from '../../components/Marquee/Marquee';
import SectionHead from '../../components/SectionHead/SectionHead';
import GrowthPillars from '../../components/GrowthPillars/GrowthPillars';
import GrowthCalculator from '../../components/GrowthCalculator/GrowthCalculator';
import GrowthJourney from '../../components/GrowthJourney/GrowthJourney';
import LiveBill from '../../components/LiveBill/LiveBill';
import Bento from '../../components/Bento/Bento';
import BeforeAfter from '../../components/BeforeAfter/BeforeAfter';
import FAQ, { FAQ_ITEMS } from '../../components/FAQ/FAQ';
import Seo, { faqLd, organizationLd, softwareLd } from '../../components/Seo/Seo';
import CTABand from '../../components/CTABand/CTABand';
import { SITE } from '../../config/site';

export default function Home() {
    return (
        <>
            <Seo jsonLd={[organizationLd(), softwareLd(), faqLd(FAQ_ITEMS)]} />
            <Hero />

            <Marquee />

            {/* ---------------- Three ways Hirsta grows a salon ---------------- */}
            <section className={styles.section}>
                <div className={styles.container}>
                    <SectionHead
                        align="center"
                        title="Software that grows your salon, not just runs it"
                        intro="Every part of Hirsta is built around three things owners care about: more revenue, a stronger brand and clients who keep coming back."
                    />
                    <GrowthPillars />
                </div>
            </section>

            {/* ---------------- Calculator ---------------- */}
            <section className={`${styles.section} ${styles.tint}`} id="growth">
                <div className={styles.container}>
                    <SectionHead title="How much more could your salon earn?" intro="Move the sliders to match your salon. The total updates as you go." />
                    <GrowthCalculator />
                </div>
            </section>

            {/* ---------------- Growth journey ---------------- */}
            <section className={`${styles.section} ${styles.dark}`}>
                <div className={styles.container}>
                    <SectionHead dark align="center" title="What changes after you switch to Hirsta" intro="From the first week to the third month, here is how salons grow with us." />
                    <GrowthJourney />
                    <div className={styles.centerCta}><Button to="/demo" variant="light" size="lg" icon={ArrowUpRight}>Start with a free demo</Button></div>
                </div>
            </section>

            {/* ---------------- Every bill sells for you ---------------- */}
            <section className={styles.section}>
                <div className={`${styles.container} ${styles.billSplit}`}>
                    <div>
                        <SectionHead title="Every bill sells and markets for you" intro="The counter is where most salons leave money behind. With Hirsta, each bill upsells, rewards loyalty and carries your brand to the client's phone." />
                        <ul className={styles.billPoints}>
                            {[
                                'Member discounts applied by themselves, so regulars feel valued',
                                'Package visits added at ₹0, keeping clients tied to your salon',
                                'The bill reaches the client on WhatsApp with your logo',
                                'Every rupee lands in the right report, ready for your CA',
                            ].map((t) => <li key={t}><Check size={14} strokeWidth={3} /> {t}</li>)}
                        </ul>
                    </div>
                    <div className={styles.billStage}><LiveBill /></div>
                </div>
            </section>

            {/* ---------------- The tools behind it ---------------- */}
            <section className={`${styles.section} ${styles.tint}`}>
                <div className={styles.container}>
                    <SectionHead title="Everything behind your growth, in one login" intro="Billing, loyalty, appointments, staff and marketing share one database, so a single bill updates them all." />
                    <Bento />
                </div>
            </section>

            {/* ---------------- Before / after ---------------- */}
            <section className={styles.section}>
                <div className={styles.container}>
                    <SectionHead align="center" title="The same salon, growing faster" intro="Switch between the two to see what changes." />
                    <BeforeAfter />
                </div>
            </section>

            {/* ---------------- FAQ ---------------- */}
            <section className={`${styles.section} ${styles.tint}`}>
                <div className={styles.container}>
                    <SectionHead align="center" title="Questions owners ask us" intro={`Anything else? Call us at ${SITE.phone}.`} />
                    <FAQ />
                </div>
            </section>

            <div className={styles.ctaGap} />
            <CTABand />
        </>
    );
}
